import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MallApiService } from '../services/auth';
import { Mall } from '../types/auth';
import {
  Building2,
  Users,
  MapPin,
  Calendar,
  RefreshCw,
  AlertCircle,
  Shield,
  UserCheck,
  Settings,
  LogOut,
  ShoppingBag,
  QrCode,
  CreditCard,
  Phone,
  DollarSign,
  CheckCircle,
  Clock,
  Inbox,
  MessageSquare,
  User,
  MessageCircle,
  ShoppingCart,
  Eye,
  X,
  BarChart3 // Added here once
} from 'lucide-react';
import { MallCard } from './MallCard';
import { Sidebar } from './Sidebar';
import NotificationCenter from './NotificationCenter';
import RealTimeNotificationCenter from './RealTimeNotificationCenter';
import SalesManagement from './SalesManagement';

// Types for orders and payments
interface OrderItem {
  id: number;
  product_id?: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  subtotal: number;
}

interface Order {
  id: number;
  shop_id: number;
  customer_phone: string;
  customer_name?: string;
  status: string;
  amount: number;
  items: OrderItem[];
  created_at: string;
}

interface PaymentTransaction {
  id: number;
  order_id: number;
  customer_phone: string;
  amount: number;
  payment_method: string;
  mpesa_receipt?: string;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  processed_at?: string;
}

interface DashboardProps {
  onViewChange: (view: string) => void;
}

export function Dashboard({ onViewChange }: DashboardProps) {
  const { user, logout } = useAuth();
  const [malls, setMalls] = useState<Mall[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  
  const [currentView, setCurrentView] = useState<'main' | 'sales'>('main');

  
  // Orders and Payments State
  const [orders, setOrders] = useState<Order[]>([]);
  const [payments, setPayments] = useState<PaymentTransaction[]>([]);
  const [isLoadingOrders, setIsLoadingOrders] = useState(false);
  const [isLoadingPayments, setIsLoadingPayments] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'orders' | 'payments'>('orders');
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [newOrderStatus, setNewOrderStatus] = useState<string>('');

  const fetchMalls = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('geofence_auth_token') || '';
      console.log('🎯 Dashboard: Fetching malls...');
      console.log('🎯 Dashboard: Retrieved token:', token ? `${token.substring(0, 20)}...` : 'EMPTY TOKEN');
      console.log('🎯 Dashboard: Token length:', token.length);
      console.log('🎯 Dashboard: Current user:', user);
      
      // Properly type the response to fix TypeScript compilation errors
      const response: { success: boolean; data?: Mall[]; error?: string } = await MallApiService.fetchMalls(token);
      console.log('🎯 Dashboard: Mall API response:', response);
      
      if (response.success && response.data) {
        // For shop_admin and shop_staff users, filter to only their assigned mall
        let filteredMalls = response.data;
        if (user && (user.role === 'shop_admin' || user.role === 'shop_staff') && user.mall_access && user.mall_access.length > 0) {
          filteredMalls = response.data.filter((mall: Mall) => user.mall_access?.includes(Number(mall.id)));
          console.log('🔒 Dashboard: Shop user - filtered to mall_access:', user.mall_access, 'Total malls:', filteredMalls.length);
        }
        
        setMalls(filteredMalls);
        console.log('🎯 Dashboard: Successfully loaded', filteredMalls.length, 'malls');
      } else {
        setMalls([]);
        setError(response.error || 'Failed to fetch malls');
        console.log('🎯 Dashboard: Failed to load malls:', response.error);
      }
    } catch (err) {
      console.error('🎯 Dashboard: Network error:', err);
      setError('Network error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  // Get the shop_id from user's shop_id field
  const getShopId = (): number => {
    // For shop_admin and shop_staff users, use the shop_id from the user object
    if (user && user.shop_id) {
      return user.shop_id;
    }
    // Fallback to shop_access array if shop_id is not directly available
    if (user && user.shop_access && user.shop_access.length > 0) {
      return user.shop_access[0];
    }
    return 0;
  };

  // Fetch orders from the get-orders webhook
  const fetchOrders = async () => {
    setIsLoadingOrders(true);
    setOrderError(null);

    const shopId = getShopId();
    
    if (shopId === 0) {
      setOrderError('No shop assigned to this user');
      setIsLoadingOrders(false);
      return;
    }

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/get-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: 'get-orders',
          shop_id: shopId,
          limit: 100
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      const data = await response.json();
      console.log('🎯 Dashboard: Orders API response:', data);
      
      if (data.success && data.data) {
        // Ensure data.data is always an array
        const ordersData = Array.isArray(data.data) ? data.data : [data.data];
        setOrders(ordersData);
      } else if (data.success && (!data.data || (Array.isArray(data.data) && data.data.length === 0))) {
        // Successful response but no orders found - this is not an error, just empty data
        setOrders([]);
        setOrderError(null); // Clear any previous error
      } else {
        setOrders([]);
        setOrderError(data.error || 'Failed to load orders');
      }
    } catch (err) {
      console.error('🎯 Dashboard: Orders fetch error:', err);
      // Check if this is an empty response error (n8n stopped workflow with no output)
      if (err instanceof SyntaxError && err.message.includes('Unexpected end of JSON input')) {
        // This typically happens when n8n returns an empty response because no data was found
        // This is NOT an error - it's an expected empty state
        console.log('🎯 Dashboard: Empty response from n8n (no orders found), treating as empty state');
        setOrders([]);
        setOrderError(null); // Clear any previous error
      } else if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setOrderError('Unable to connect to the server. The server may be down or blocking requests.');
      } else {
        // For any other error, treat as empty state to avoid alarmist messages
        console.log('🎯 Dashboard: Order fetch failed, treating as empty state to avoid alarmist messages');
        setOrders([]);
        setOrderError(null); // Don't show alarmist errors for expected empty states
      }
    } finally {
      setIsLoadingOrders(false);
    }
  };

  // Fetch payments from the get-payments webhook
  const fetchPayments = async () => {
    setIsLoadingPayments(true);
    setPaymentError(null);

    const shopId = getShopId();
    
    if (shopId === 0) {
      setPaymentError('No shop assigned to this user');
      setIsLoadingPayments(false);
      return;
    }

    try {
      const response = await fetch('https://n8n.tenear.com/webhook/get-payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          path: 'get-payments',
          shop_id: shopId,
          limit: 100
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to fetch payments');
      }

      const data = await response.json();
      console.log('🎯 Dashboard: Payments API response:', data);
      
      if (data.success && data.data) {
        // Ensure data.data is always an array
        const paymentsData = Array.isArray(data.data) ? data.data : [data.data];
        setPayments(paymentsData);
      } else if (data.success && (!data.data || (Array.isArray(data.data) && data.data.length === 0))) {
        // Successful response but no payments found - this is not an error, just empty data
        setPayments([]);
        setPaymentError(null); // Clear any previous error
      } else {
        setPayments([]);
        setPaymentError(data.error || 'Failed to load payments');
      }
    } catch (err) {
      console.error('🎯 Dashboard: Payments fetch error:', err);
      // Check if this is an empty response error (n8n stopped workflow with no output)
      if (err instanceof SyntaxError && err.message.includes('Unexpected end of JSON input')) {
        // This typically happens when n8n returns an empty response because no data was found
        // This is NOT an error - it's an expected empty state
        console.log('🎯 Dashboard: Empty response from n8n (no payments found), treating as empty state');
        setPayments([]);
        setPaymentError(null); // Clear any previous error
      } else if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        setPaymentError('Unable to connect to the server. The server may be down or blocking requests.');
      } else {
        // For any other error, check if we might have partial data
        console.log('🎯 Dashboard: Payment fetch failed, treating as empty state to avoid alarmist messages');
        setPayments([]);
        setPaymentError(null); // Don't show alarmist errors for expected empty states
      }
    } finally {
      setIsLoadingPayments(false);
    }
  };

  // Combined fetch for all shop data
  const fetchShopData = async () => {
    if (user && (user.role === 'shop_admin' || user.role === 'shop_staff')) {
      // Reset to orders tab for shop_staff to prevent access to payments
      if (user.role === 'shop_staff') {
        setActiveTab('orders');
      }
      await Promise.all([
        fetchOrders(),
        user.role === 'shop_admin' ? fetchPayments() : Promise.resolve()
      ]);
    }
  };

  // Update order status
  const updateOrderStatus = async (orderId: number, newStatus: string) => {
    try {
      const response = await fetch('https://n8n.tenear.com/webhook/update-order-status', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          order_id: orderId,
          status: newStatus
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to update order status');
      }

      // Refresh orders after update
      await fetchOrders();
      
      // Close modal
      setSelectedOrder(null);
      setNewOrderStatus(''); // Reset the status dropdown
    } catch (err) {
      console.error('Error updating order status:', err);
      // Provide more specific error messages
      if (err instanceof TypeError && err.message.includes('Failed to fetch')) {
        alert('Unable to connect to the server. This may be due to CORS configuration. Please contact your administrator.');
      } else {
        alert('Failed to update order status. Please try again.');
      }
    }
  };


  useEffect(() => {
    if (user) {
      fetchMalls();
      fetchShopData();
    }
  }, [user]);

  const handleRefresh = () => {
    fetchMalls();
    fetchShopData();
  };

  // Calculate real-time stats from actual payment data
  const calculateStats = () => {
    const today = new Date().toDateString();
    
    // Filter today's payments
    const todayPayments = payments.filter(p => 
      new Date(p.created_at).toDateString() === today
    );
    
    const totalRevenue = todayPayments
      .filter(p => p.status === 'completed')
      .reduce((sum, p) => sum + p.amount, 0);
    
    const successfulCount = todayPayments.filter(p => 
      p.status === 'completed'
    ).length;
    
    const pendingCount = todayPayments.filter(p => 
      p.status === 'pending'
    ).length;

    return {
      totalRevenue,
      successfulCount,
      pendingCount
    };
  };

  const stats = calculateStats();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
      case 'paid':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'failed':
      case 'cancelled':
        return <X className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-600" />;
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-KE', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'super_admin':
        return <Shield className="w-5 h-5" />;
      case 'mall_admin':
        return <Building2 className="w-5 h-5" />;
      case 'shop_admin':
        return <ShoppingBag className="w-5 h-5" />;
      case 'shop_staff':
        return <Users className="w-5 h-5" />;
      default:
        return <UserCheck className="w-5 h-5" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'super_admin':
        return 'text-primary-500';
      case 'mall_admin':
        return 'text-success';
      case 'shop_admin':
        return 'text-warning';
      case 'shop_staff':
        return 'text-blue-500';
      default:
        return 'text-text-secondary';
    }
  };

  if (!user) return null;

  // Render Orders Table
  const renderOrdersTable = () => {
    if (isLoadingOrders) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-text-secondary">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading orders...</span>
          </div>
        </div>
      );
    }

    if (orderError) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-amber-800">{orderError}</p>
              {orderError.includes('Network error') ? (
                <p className="text-amber-600 text-sm mt-1">
                  Unable to connect to the server. Please check your internet connection and try again.
                </p>
              ) : (
                <p className="text-amber-600 text-sm mt-1">
                  Please try again or contact support if the problem persists.
                </p>
              )}
              <button
                onClick={fetchOrders}
                className="mt-2 text-amber-600 underline hover:no-underline text-sm"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (orders.length === 0) {
      return (
        <div className="text-center py-12">
          <ShoppingCart className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No orders yet
          </h3>
          <p className="text-gray-500">
            Orders from customers will appear here once they start coming in.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm font-medium text-gray-500 border-b border-gray-200">
              <th className="pb-3 pr-4">Order ID</th>
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">Items</th>
              <th className="pb-3 pr-4">Total</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3">Date</th>
              <th className="pb-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {orders.map((order) => (
              <tr key={order.id} className="hover:bg-gray-50">
                <td className="py-3 pr-4">
                  <span className="font-medium text-gray-900">#{order.id}</span>
                </td>
                <td className="py-3 pr-4">
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {order.customer_name || 'Guest'}
                    </p>
                    <p className="text-xs text-gray-500">{order.customer_phone}</p>
                  </div>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm text-gray-600">
                    {Array.isArray(order.items) ? order.items.length : 0} item{(Array.isArray(order.items) ? order.items.length : 0) !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(order.amount)}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                    {order.status}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm text-gray-500">
                    {formatDate(order.created_at)}
                  </span>
                </td>
                <td className="py-3">
                  <button
                    onClick={() => setSelectedOrder(order)}
                    className="p-1 text-gray-400 hover:text-gray-600 rounded"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Render Payments Table
  const renderPaymentsTable = () => {
    if (isLoadingPayments) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="flex items-center gap-3 text-text-secondary">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading payments...</span>
          </div>
        </div>
      );
    }

    if (paymentError) {
      return (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-amber-800">{paymentError}</p>
              {paymentError.includes('Network error') ? (
                <p className="text-amber-600 text-sm mt-1">
                  Unable to connect to the server. Please check your internet connection and try again.
                </p>
              ) : (
                <p className="text-amber-600 text-sm mt-1">
                  Please try again or contact support if the problem persists.
                </p>
              )}
              <button
                onClick={fetchPayments}
                className="mt-2 text-amber-600 underline hover:no-underline text-sm"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      );
    }

    if (payments.length === 0) {
      return (
        <div className="text-center py-12">
          <CreditCard className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No payments yet
          </h3>
          <p className="text-gray-500">
            Payment transactions will appear here once customers start paying.
          </p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-sm font-medium text-gray-500 border-b border-gray-200">
              <th className="pb-3 pr-4">TXN ID</th>
              <th className="pb-3 pr-4">Order ID</th>
              <th className="pb-3 pr-4">Phone</th>
              <th className="pb-3 pr-4">Amount</th>
              <th className="pb-3 pr-4">Method</th>
              <th className="pb-3 pr-4">Receipt</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3">Date</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {payments.map((payment) => (
              <tr key={payment.id} className="hover:bg-gray-50">
                <td className="py-3 pr-4">
                  <span className="font-medium text-gray-900 text-sm">#{payment.id}</span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm text-gray-600">#{payment.order_id}</span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm text-gray-900">{payment.customer_phone}</span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm font-medium text-gray-900">
                    {formatCurrency(payment.amount)}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm text-gray-600">{payment.payment_method}</span>
                </td>
                <td className="py-3 pr-4">
                  <span className="text-sm text-gray-500">
                    {payment.mpesa_receipt || '-'}
                  </span>
                </td>
                <td className="py-3 pr-4">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(payment.status)}`}>
                    {getStatusIcon(payment.status)}
                    {payment.status}
                  </span>
                </td>
                <td className="py-3">
                  <span className="text-sm text-gray-500">
                    {formatDate(payment.created_at)}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-page-bg flex">
      {/* Sidebar */}
      <Sidebar onLogout={logout} onViewChange={onViewChange} />
      
      {/* Main Content */}

    <div className="p-6"> {/* Main container */}  
      {currentView === 'main' ? (
      <>
        {/* Orders Dashboard */}
        {user.role === 'shop_staff' && (
          <div className="mb-8">
             {/* ... your Orders Dashboard code ... */}
             {renderOrdersTable()}
          </div>
        )}

        {/* QR Code Generation */}
        <div className="mb-8">
           {/* ... your QR Code code ... */}
        </div>

        {/* Campaign Management */}
        {(user.role === 'shop_admin' || user.role === 'shop_staff') && (
          <div className="mb-8">
             {/* ... your Campaign code ... */}
          </div>
        )}
       

      </>
    ) : null} 
  </div>
);



      <div className="flex-1 lg:ml-64">
        {/* Header */}
        <header className="bg-surface-bg border-b border-border-subtle">
          <div className="container py-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  Dashboard
                </h1>
 
               <p className="text-text-secondary mt-1">
                  Welcome back, {user.full_name}
                </p>
              </div>
              
              <div className="flex items-center gap-4">
                {/* Notification Centers - Side by Side Test */}
                {user.mall_access && user.mall_access.length > 0 && (
                  <div className="flex items-center gap-2">
                    {/* OLD Polling System */}
                    <NotificationCenter user={user} />
                    
                    {/* NEW Real-Time System */}
                    <RealTimeNotificationCenter user={user} />
                  </div>
                )}
                
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  disabled={isLoading}
                  className="btn-secondary flex items-center gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${isLoading || isLoadingOrders || isLoadingPayments ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
                
                {/* User Menu */}
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center gap-3 bg-page-bg px-4 py-2 rounded-md hover:bg-gray-100 transition-colors"
                  >
                    <div className="text-right">
                      <p className="text-sm font-medium text-text-primary">
                        {user.full_name}
                      </p>
                      <div className="flex items-center gap-1">
                        {getRoleIcon(user.role)}
                        <span className={`text-xs font-medium ${getRoleColor(user.role)}`}>
                          {user.role.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <User className="w-4 h-4 text-gray-500" />
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  {showUserMenu && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                      <div className="px-4 py-2 border-b border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                        <p className="text-xs text-gray-500">{user.username}</p>
                      </div>
                      <button
                        onClick={() => {
                          logout();
                          setShowUserMenu(false);
                        }}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign Out
                      </button>
                    </div>
                  )}
                  
                  {/* Click outside to close */}
                  {showUserMenu && (
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowUserMenu(false)}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content Area */}
        <main className="container py-8">
          {/* Role-based greeting - Simplified for shop admins and staff */}
          <div className="mb-8">
            <div className="bg-surface-bg border border-border-subtle rounded-md p-6">
              <div className="flex items-start gap-4">
                <div className="bg-primary-100 p-3 rounded-lg">
                  {getRoleIcon(user.role)}
                </div>
                <div className="flex-1">
                  <h2 className="text-lg font-semibold text-text-primary mb-2">
                    {user.role === 'super_admin' && 'Full System Access'}
                    {user.role === 'mall_admin' && 'Mall Management'}
                    {user.role === 'shop_admin' && `${user.full_name?.split(' - ')[0] || 'Shop Owner'} Portal`}
                    {user.role === 'shop_staff' && `${user.full_name?.split(' - ')[0] || 'Staff'} Portal`}
                  </h2>
                  <p className="text-text-secondary">
                    {user.role === 'super_admin' && 'You have access to all malls and shops in the system. You can view, manage, and configure all locations.'}
                    {user.role === 'mall_admin' && `You are the administrator for your assigned mall. You can manage all shops within your location.`}
                    {user.role === 'shop_admin' && `Welcome to your shop management dashboard. Here you can create campaigns, track performance, manage orders, and view payment analytics.`}
                    {user.role === 'shop_staff' && `Welcome to your staff dashboard. Here you can view campaigns, engage with visitors, manage customer inquiries, and track orders.`}
                  </p>
                  {user.mall_access && user.mall_access.length > 0 && user.role !== 'shop_admin' && user.role !== 'shop_staff' && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-text-secondary">
                      <MapPin className="w-4 h-4" />
                      Assigned Mall ID: {user.mall_access[0]}
                    </div>
                  )}
                  {(user.role === 'shop_admin' || user.role === 'shop_staff') && user.mall_access && user.mall_access.length > 0 && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-text-secondary">
                      <MapPin className="w-4 h-4" />
                      Located at: {user.full_name?.split(' - ')[1] || 'Your Mall'} (Mall ID: {user.mall_access[0]})
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Orders & Payments Dashboard - For shop_admin ONLY */}
          {user.role === 'shop_admin' && (
            <div className="mb-8">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg">
                      <ShoppingCart className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Orders & Payments
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Monitor customer orders, track payments, and manage transaction history
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={fetchShopData}
                      disabled={isLoadingOrders || isLoadingPayments}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoadingOrders || isLoadingPayments ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>
                
                {/* Real Stats - Computed from actual payment data */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Today's Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
                      </div>
                      <DollarSign className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  
                  <div className="bg-white/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Successful Payments</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.successfulCount}</p>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                  </div>
                  
                  <div className="bg-white/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Pending</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.pendingCount}</p>
                      </div>
                      <Clock className="w-8 h-8 text-yellow-600" />
                    </div>
                  </div>

                  <div className="bg-white/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Total Orders</p>
                        <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
                      </div>
                      <ShoppingCart className="w-8 h-8 text-indigo-600" />
                    </div>
                  </div>
                </div>

                {/* Tab Navigation */}
                <div className="border-b border-gray-200 mb-4">
                  <div className="flex gap-4">
                    <button
                      onClick={() => setActiveTab('orders')}
                      className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                        activeTab === 'orders'
                          ? 'border-indigo-600 text-indigo-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      <ShoppingCart className="w-4 h-4 inline mr-2" />
                      Orders ({orders.length})
                    </button>
                    {user.role === 'shop_admin' && (
                      <button
                        onClick={() => setActiveTab('payments')}
                        className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === 'payments'
                            ? 'border-indigo-600 text-indigo-600'
                            : 'border-transparent text-gray-500 hover:text-gray-700'
                        }`}
                      >
                        <CreditCard className="w-4 h-4 inline mr-2" />
                        Payments ({payments.length})
                      </button>
                    )}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="bg-white/50 rounded-lg p-4">
                  {activeTab === 'orders' && renderOrdersTable()}
                  {activeTab === 'payments' && user.role === 'shop_admin' && renderPaymentsTable()}
                </div>
              </div>
            </div>
          )}

          {/* Orders Dashboard - For shop_staff ONLY (no payment access) */}
          {user.role === 'shop_staff' && (
            <div className="mb-8">
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="bg-indigo-100 p-3 rounded-lg">
                      <ShoppingCart className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        Orders
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Track customer orders
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={fetchOrders}
                      disabled={isLoadingOrders}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <RefreshCw className={`w-4 h-4 ${isLoadingOrders ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                  </div>
                </div>

                {/* Simple order count - no payment/revenue data */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white/50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600">Total Orders</p>
                        <p className="text-2xl font-bold text-gray-900">{orders.length}</p>
                      </div>
                      <ShoppingCart className="w-8 h-8 text-indigo-600" />
                    </div>
                  </div>
                </div>

                {/* Orders Table Only */}
                <div className="bg-white/50 rounded-lg p-4">
                  {renderOrdersTable()}
                </div>
              </div>
            </div>
          )}

          {/* QR Code Generation - For All User Roles */}
          <div className="mb-8">
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 p-3 rounded-lg">
                    <QrCode className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      QR Code Generation
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Generate QR codes for visitor check-ins, campaigns, and location-specific engagement
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => onViewChange?.('qr-generation')}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  <QrCode className="w-5 h-5" />
                  Generate QR Codes
                </button>
              </div>
            </div>
          </div>

          {/* Campaign Management for Shop Admins and Shop Staff */}
          {(user.role === 'shop_admin' || user.role === 'shop_staff') && (
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-blue-100 p-3 rounded-lg">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                        </svg>
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Campaign Management
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Create promotional campaigns, track customer engagement, and manage visitor analytics
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onViewChange?.('campaigns')}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Manage Campaigns
                    </button>
                  </div>
                </div>
                       
              </div>
            </div>
          )}

          {/* Sales Management Card */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="bg-blue-100 p-3 rounded-lg">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-1">Sales Management</h3>
                  <p className="text-gray-600 text-sm">
                    Extract periodic sales data, print reports, and query your sales databases.
                  </p>
                </div>
              </div>

              <div className="layout">
                {currentView === 'main' ? (
                  <div className="dashboard-grid">
                     {/* ... Sales Management Card ... */}
                     <button 
                       onClick={() => {
                         setCurrentView('sales');
                                  
                       }}
                       className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
                     >
                       <BarChart3 className="w-5 h-5" />
                       Manage Sales
                     </button>
                  </div>
                ) : currentView === 'sales' && (
                  <SalesManagement 
                    shopId={getShopId()} // 3. Pass the props required
                    onBack={() => setCurrentView('main')} 
                  />
                )}
              </div>


            </div>
          </div>


          {/* Payment Modules for Shop Admins */}
          {user.role === 'shop_admin' && (
            <div className="mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Payment Reception Module */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-green-100 p-3 rounded-lg">
                        <Inbox className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          Payment Reception
                        </h3>
                        <p className="text-gray-600 text-sm">
                          Monitor incoming MPESA payments, track transactions, and manage real-time payment reception
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => onViewChange?.('payment-reception')}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-medium transition-colors flex items-center gap-2"
                    >
                      <Inbox className="w-5 h-5" />
                      View Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
                  

      {/* Product Management Card */}
      <div className="bg-blue-50 p-6 rounded-xl border border-blue-100 flex justify-between items-center">
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-3">
            <div className="bg-blue-500 p-2 rounded-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-800">Product Management</h3>
          </div>
          <p className="text-gray-600 max-w-md">
            Upload new products, manage inventory, and track customer interactions (Inquire, Purchase, Book).
          </p>
        </div>
        <button 
          onClick={() => {
            console.log("!!! CLICKED PRODUCT MGMT !!!");
            onViewChange('products');
          }} 
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center gap-2"
        >
          Manage Products
        </button>
      </div>

 

          </main>       
        </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">
                  Order #{selectedOrder.id} Details
                </h2>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm text-gray-600">Customer</p>
                    <p className="font-medium text-gray-900">
                      {selectedOrder.customer_name || 'Guest'}
                    </p>
                    <p className="text-sm text-gray-500">{selectedOrder.customer_phone}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Status</p>
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(selectedOrder.status)}`}>
                      {selectedOrder.status}
                    </span>
                    <div className="mt-2">
                      <select
                        value={newOrderStatus}
                        onChange={(e) => setNewOrderStatus(e.target.value)}
                        className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                      >
                        <option value="">Select new status</option>
                        <option value="pending">Pending</option>
                        <option value="processing">Processing</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-3">Order Items</h3>
                  <div className="space-y-2">
                    {(selectedOrder.items || []).map((item, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="font-medium text-gray-900">{item.product_name}</p>
                          <p className="text-sm text-gray-500">
                            {item.quantity} × {formatCurrency(item.unit_price)}
                          </p>
                        </div>
                        <span className="font-medium text-gray-900">
                          {formatCurrency(item.subtotal)}
                        </span>
                      </div>
                    ))}
                    {(!selectedOrder.items || selectedOrder.items.length === 0) && (
                      <p className="text-sm text-gray-500 text-center py-4">No items in this order</p>
                    )}
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold text-gray-900">Total</span>
                    <span className="text-xl font-bold text-indigo-600">
                      {formatCurrency(selectedOrder.amount)}
                    </span>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-4">
                  <p className="text-xs text-gray-500">
                    Created: {formatDate(selectedOrder.created_at)}
                  </p>
                </div>
              </div>

              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    if (selectedOrder && newOrderStatus) {
                      updateOrderStatus(selectedOrder.id, newOrderStatus);
                    } else {
                      alert('Please select a new status');
                    }
                  }}
                  disabled={!newOrderStatus}
                  className={`flex-1 px-4 py-2 text-white rounded-lg transition-colors ${
                    newOrderStatus 
                      ? 'bg-indigo-600 hover:bg-indigo-700' 
                      : 'bg-indigo-300 cursor-not-allowed'
                  }`}
                >
                  Update Status
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
